import { Link, useRoute } from "@/lib/router";
import { Card, CardContent } from "@/components/ui/card";
import {
  LayoutDashboard, FolderTree, FileText, ShieldAlert, KeyRound,
  Users, Settings, BarChart3, CreditCard, Wrench, BookOpen, Coins,
  Megaphone, CalendarCheck, Ticket, Inbox
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "仪表盘", icon: LayoutDashboard },
  { to: "/admin/categories", label: "栏目管理", icon: FolderTree },
  { to: "/admin/projects", label: "内容管理", icon: FileText },
  { to: "/admin/paywall", label: "付费规则", icon: ShieldAlert },
  { to: "/admin/plans", label: "会员套餐", icon: CreditCard },
  { to: "/admin/point-packages", label: "积分套餐", icon: Coins },
  { to: "/admin/checkin", label: "签到规则", icon: CalendarCheck },
  { to: "/admin/announcements", label: "公告管理", icon: Megaphone },
  { to: "/admin/redeem-codes", label: "兑换码", icon: Ticket },
  { to: "/admin/inbox-send", label: "站内信发送", icon: Inbox },
  { to: "/admin/users", label: "用户管理", icon: Users },
  { to: "/admin/api-keys", label: "API 密钥", icon: KeyRound },
  { to: "/admin/crawler-docs", label: "爬虫开发文档", icon: BookOpen },
  { to: "/admin/analytics", label: "数据分析", icon: BarChart3 },
  { to: "/admin/settings", label: "站点设置", icon: Settings },
  { to: "/admin/maintenance", label: "维护模式", icon: Wrench },
];

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const route = useRoute();
  return (
    <div className="container mx-auto px-4 py-6 grid lg:grid-cols-[220px_1fr] gap-6">
      <aside>
        <Card>
          <CardContent className="p-2">
            <nav className="flex flex-col">
              {NAV.map((n) => {
                const active = route.path === n.to || (n.to !== "/admin" && route.path.startsWith(n.to));
                const Icon = n.icon;
                return (
                  <Link key={n.to} to={n.to}
                    className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                      active ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground hover:text-foreground"
                    }`}>
                    <Icon className="h-4 w-4" />
                    {n.label}
                  </Link>
                );
              })}
            </nav>
          </CardContent>
        </Card>
      </aside>
      <section>{children}</section>
    </div>
  );
}

export function AdminDashboard() {
  return (
    <AdminLayout>
      <Card>
        <CardContent className="p-6 space-y-2">
          <h1 className="text-xl font-semibold">仪表盘</h1>
          <p className="text-sm text-muted-foreground">
            欢迎回到管理后台。在左侧菜单中管理栏目、内容、付费规则和站点设置。
          </p>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}

export function AdminPlaceholder({ title, hint }: { title: string; hint: string }) {
  return (
    <AdminLayout>
      <Card>
        <CardContent className="p-6 space-y-2">
          <h1 className="text-xl font-semibold">{title}</h1>
          <p className="text-sm text-muted-foreground">{hint}</p>
          <div className="text-xs text-muted-foreground pt-3 border-t mt-4">
            后端 API 已就绪：<code className="rounded bg-muted px-1 py-0.5 font-mono">/api/admin/*</code>。
            UI 表单将在 M6 里程碑实现，目前可使用 curl/Postman 或 admin Swagger 调用。
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
